/**
 * Der Betreff der Angebots-E-Mail — EINE Quelle fuer beide Seiten.
 *
 * Auftrag (UX-Polish Paket B4, docs/UX-POLISH-UMSETZUNGSPLAN.md): Betreff neu
 * „Ihr Angebot für Ihre Feier am [Datum] ([Gästezahl] Gäste)" — **an einer Stelle**, nicht an
 * zweien.
 *
 * ── Was vorher da war, und warum es auseinanderlief ──────────────────────────────────────────
 * Bis zum 25.08.2026 gab es ZWEI Betreff-Quellen, die einander nicht kannten:
 *   1. `buildOfferEmail()` in `apps/api/src/lib/mailer.ts` — „Ihr Angebot für <Anlass> – <Betrieb>".
 *      Diese Fassung ging bei jedem Versand raus, den KEIN Frontend ausloest.
 *   2. `openSend()` in `apps/web/src/pages/builder.tsx` — „Ihr Angebot für Ihr <Anlass> am
 *      <Tag. Monat>". Sie ueberschrieb (1) beim Versand aus dem Builder ueber `overrideSubject`
 *      (`POST /api/events/:id/offers/send`).
 * Zwei Formulierungen, zwei Datumsformate, zwei Pflegeorte — der Kunde bekam je nach Absendeweg
 * einen anderen Betreff, und eine Aenderung an einer Stelle blieb an der anderen unbemerkt.
 *
 * ── Warum das BACKEND die Wahrheit traegt (und dieses Paket sie ihm liefert) ──────────────────
 * Nicht jeder Versand hat ein Frontend: die Nachfass-Sweeps, die Erinnerungen und der Resend
 * laufen im Cron bzw. serverseitig. Eine Betreff-Regel, die im Browser wohnt, ist fuer diese
 * Wege schlicht nicht erreichbar — sie MUSS also serverseitig gelten. Umgekehrt soll der
 * Betreiber den Betreff im Senden-Dialog SEHEN und aendern koennen, bevor er sendet; ein leeres
 * Feld mit „wird schon passen" waere ein Rueckschritt.
 *
 * Beides zusammen geht nur, wenn dieselbe Funktion an beiden Orten laeuft. Darum ein eigenes
 * Workspace-Paket (Hausmuster: `@maestro/transparenz-hinweis`, `@maestro/recherche-stand`,
 * `@maestro/anfrage-stand`): `apps/api` und `apps/web` importieren es beide, und der
 * Frontend-Override schickt kuenftig genau den Text mit, den das Backend ohnehin gebaut haette —
 * er ist nur noch der Weg fuer die HANDBEARBEITUNG, nicht mehr eine zweite Formulierung.
 * `packages/*` darf weder auf `apps/api` noch auf `apps/web` zeigen; die Abhaengigkeit laeuft
 * also in die richtige Richtung.
 *
 * ── Warum eigene Monatsnamen statt `toLocaleDateString` ──────────────────────────────────────
 * Zwei Gruende, beide praktisch:
 *   * ZEITZONE. `new Date("2026-08-15")` ist UTC-Mitternacht. `toLocaleDateString()` ohne
 *     `timeZone` formatiert in der ORTSZEIT — westlich von Greenwich wird daraus der 14. August.
 *     Der Worker laeuft in UTC, der Browser des Betreibers nicht; genau so entstuende wieder ein
 *     Unterschied zwischen den beiden Seiten, den dieses Paket beseitigen soll.
 *   * DETERMINISMUS. Die Monatsnamen kommen sonst aus der ICU-Datenbank der jeweiligen Laufzeit
 *     (Node, Workers, Browser) und koennen sich zwischen Versionen unterscheiden. Zwoelf
 *     Zeichenketten sind billiger als diese Unsicherheit — und im Test nachlesbar.
 * Das ISO-Datum wird deshalb woertlich zerlegt (`YYYY-MM-DD`), ohne je ein `Date` zu bauen.
 */

/** Sprachen, die die Angebots-Mail kennt (identisch mit `OfferEmailInput.lang` in lib/mailer). */
export type BetreffSprache = "de" | "en";

const MONATE: Record<BetreffSprache, readonly string[]> = {
  de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};

/**
 * „15. August 2026" (de) bzw. „15 August 2026" (en) aus einem ISO-Datum.
 *
 * Nimmt `YYYY-MM-DD` und ebenso den vollen Zeitstempel `YYYY-MM-DDTHH:MM:SSZ` (die Spalte
 * `events.event_date` ist ein DATE, andere Aufrufer reichen aber schon mal mehr herein) — es
 * zaehlt ausschliesslich der Datumsteil davor. Alles, was nicht so aussieht, ergibt `null`; der
 * Betreff laesst den Datumsteil dann weg, statt „Invalid Date" zu verschicken.
 */
export function betreffDatum(iso: string | null | undefined, lang: BetreffSprache = "de"): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!m) return null;
  const jahr = Number(m[1]);
  const monat = Number(m[2]);
  const tag = Number(m[3]);
  if (monat < 1 || monat > 12 || tag < 1 || tag > 31) return null;
  const monatsname = MONATE[lang][monat - 1]!;
  return lang === "de" ? `${tag}. ${monatsname} ${jahr}` : `${tag} ${monatsname} ${jahr}`;
}

/**
 * Die Gaestezahl fuer die Anzeige: aus den Gaestezahlen der VARIANTEN, weil auf genau diese Zahl
 * der Preis gerechnet ist (§5.19) — `events.guest_count` ist nur der Rueckfall.
 *
 * Mehrere verschiedene Zahlen ergeben eine Spanne („30–40"), doppelte zaehlen einmal, Nullen und
 * negative Werte fallen raus. Gibt es gar nichts Brauchbares, ist die Antwort `null` und der
 * Betreff laesst die Klammer weg.
 *
 * Wohnte bis zum 25.08.2026 als `gaestezahlAusVarianten` in `apps/api/src/lib/mailer.ts` und ist
 * hierher gezogen, damit der Betreff im Browser dieselbe Zahl bildet wie der im Worker;
 * `lib/mailer.ts` re-exportiert sie unter dem alten Namen weiter (die Details-Box der Mail
 * benutzt sie ebenfalls).
 */
export function gaestezahlText(optionGuestCounts: number[] | null | undefined, fallback: number | null | undefined): string | null {
  const counts = [...new Set((optionGuestCounts ?? []).filter((n) => Number.isFinite(n) && n > 0))].sort((a, b) => a - b);
  if (counts.length === 1) return String(counts[0]);
  if (counts.length > 1) return `${counts[0]}–${counts[counts.length - 1]}`;
  return typeof fallback === "number" && fallback > 0 ? String(fallback) : null;
}

export interface AngebotsBetreffEingabe {
  lang: BetreffSprache;
  /** ISO-Datum der Veranstaltung (`events.event_date`). Fehlt es, entfaellt der „am …"-Teil. */
  eventDate?: string | null;
  /** Gaestezahlen der aktiven Varianten — die Zahlen, auf die der Preis gerechnet ist (§5.19). */
  optionGuestCounts?: number[] | null;
  /** Rueckfall, wenn keine Variante eine brauchbare Gaestezahl traegt (`events.guest_count`). */
  guestCount?: number | null;
  /**
   * Buchungsstrecke A3: der Anlass des Vorgangs (`events.occasion`, Freitext). Nur wenn
   * `anlassPhrase()` ihn sauber in den Satz bekommt, ersetzt er „Ihre Veranstaltung“.
   */
  occasion?: string | null;
  /**
   * Buchungsstrecke B3: der Name des Gastes (`customers.name`) vorn im Betreff — „Julia Berger, Ihr
   * Angebot für …“. Wie die Anrede (A3, `sieAnrede`) der volle gepflegte Name, kein geratener
   * Vorname; leer oder der Platzhalter „Kunde“ → Betreff ohne Namen, wie bisher.
   */
  kundenName?: string | null;
}

/** Der Name für den Betreff, oder `null` (leer, Platzhalter, zu lang oder mit Steuerzeichen). */
export function betreffName(name: string | null | undefined): string | null {
  const n = (name ?? "").replace(/\s+/g, " ").trim();
  if (!n || n.length > 60) return null;
  if (/^(kunde|kundin|customer|gast|guest)$/i.test(n)) return null;
  if (/[\u0000-\u001f<>]/.test(n)) return null;
  return n;
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Buchungsstrecke A3 (docs/LOOP-BUCHUNGSSTRECKE.md, 29.09.2026): der ECHTE Anlass statt „Feier“.
 *
 * `events.occasion` ist Freitext („geburtstag“, „60. Geburtstag“, „Event-Anfrage: Firma XY“,
 * „runder Geburtstag mit Familie“ …). Im Satz „Ihr Angebot für ___“ braucht er das richtige
 * Possessivpronomen (Ihren Geburtstag / Ihre Hochzeit / Ihr Sommerfest) — und ein falsch
 * gebeugter Anlass („Ihre Geburtstag“, „Ihren Runder Geburtstag“) wirkt schlimmer als gar keiner.
 * Darum KONSERVATIV: nur kurze, saubere Anlaesse, deren Geschlecht am Wortende sicher erkennbar ist
 * (das Grundwort eines Kompositums bestimmt das Geschlecht: Weihnachts|feier = die). Alles andere
 * ergibt `null`, und der Satz nimmt das neutrale „Ihre Veranstaltung“ / „your event“.
 * Nichts wird erfunden oder umformuliert — nur gesaeubert und gebeugt.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

type Genus = "m" | "f" | "n";

/** Wortenden -> Geschlecht. Laengste passende Endung gewinnt (Geschäfts|essen vor …en). */
const GENUS_ENDUNGEN: readonly (readonly [string, Genus])[] = [
  // maskulin
  ["geburtstag", "m"], ["tag", "m"], ["abend", "m"], ["empfang", "m"], ["brunch", "m"], ["lunch", "m"],
  ["umtrunk", "m"], ["stammtisch", "m"], ["workshop", "m"], ["ausflug", "m"], ["abschied", "m"],
  ["ball", "m"], ["kongress", "m"], ["leichenschmaus", "m"], ["besuch", "m"],
  // feminin
  ["feier", "f"], ["hochzeit", "f"], ["taufe", "f"], ["kommunion", "f"], ["konfirmation", "f"],
  ["firmung", "f"], ["jugendweihe", "f"], ["party", "f"], ["ung", "f"], ["tion", "f"], ["konferenz", "f"],
  ["gala", "f"], ["messe", "f"], ["probe", "f"], ["zeremonie", "f"], ["vernissage", "f"], ["tafel", "f"],
  // neutral
  ["fest", "n"], ["jubiläum", "n"], ["event", "n"], ["essen", "n"], ["dinner", "n"], ["meeting", "n"],
  ["seminar", "n"], ["treffen", "n"], ["catering", "n"], ["buffet", "n"], ["büfett", "n"], ["frühstück", "n"],
  ["barbecue", "n"], ["grillen", "n"], ["wiedersehen", "n"], ["konzert", "n"], ["picknick", "n"], ["bankett", "n"],
];

function genusVon(wort: string): Genus | null {
  const w = wort.toLocaleLowerCase("de-DE");
  let best: readonly [string, Genus] | null = null;
  for (const e of GENUS_ENDUNGEN) {
    if (w.endsWith(e[0]) && (!best || e[0].length > best[0].length)) best = e;
  }
  return best ? best[1] : null;
}

/** Englische Anlass-Woerter, die ein englischsprachiger Gast so schreibt (dann bleiben sie stehen). */
const EN_WOERTER = new Set([
  "birthday", "wedding", "party", "dinner", "lunch", "brunch", "event", "celebration", "christening", "baptism",
  "anniversary", "meeting", "reception", "conference", "seminar", "workshop", "communion", "confirmation",
  "engagement", "gala", "christmas", "summer", "company", "office", "team", "family", "reunion", "graduation",
  "retirement", "farewell", "buffet", "barbecue", "bbq", "get-together",
]);

/** Die haeufigen deutschen Anlaesse in der englischen Fassung (nur Einzelwort, exakt). */
const DE_EN: Record<string, string> = {
  geburtstag: "birthday", hochzeit: "wedding", weihnachtsfeier: "Christmas party", firmenfeier: "company party",
  betriebsfeier: "staff party", sommerfest: "summer party", taufe: "christening", kommunion: "communion",
  konfirmation: "confirmation", jubiläum: "anniversary", firmenjubiläum: "company anniversary",
  hochzeitstag: "wedding anniversary", verlobung: "engagement", abendessen: "dinner", feier: "celebration",
  firmenevent: "company event", geschäftsessen: "business dinner", familienfeier: "family celebration",
  abschiedsfeier: "farewell party", weihnachtsessen: "Christmas dinner", tagung: "conference",
};

function englischeOrdnungszahl(n: number): string {
  const rest100 = n % 100;
  if (rest100 >= 11 && rest100 <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
}

const WORT = /^[A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß-]*$/;
const ORDNUNGSZAHL = /^(\d{1,3})\.$/;
/** Ein Wort vor dem Grundwort, das wie ein gebeugtes Adjektiv endet („runder“, „goldene“). */
const ADJEKTIV_ENDUNG = /(e|er|es|en|em)$/i;
export const ANLASS_MAX = 40;

/** Artikel/Possessiva am Anfang eines Freitexts („unsere Weihnachtsfeier“) — das Pronomen setzt der Satz. */
const FUEHRENDES_WORT = /^(der|die|das|den|dem|ein|eine|einen|mein|meine|meinen|unser|unsere|unseren|ihr|ihre|ihren|my|our|a|an|the)\s+/i;

export interface AnlassPhrase {
  /** Der gesaeuberte Anlass („60. Geburtstag“, „birthday“). */
  anlass: string;
  /** „Ihr 60. Geburtstag“ / „your 60th birthday“ — fuer Ueberschriften. */
  nominativ: string;
  /** „Ihren 60. Geburtstag“ / „your 60th birthday“ — nach „für“ / „auf“. */
  akkusativ: string;
}

/**
 * Macht aus dem Freitext-Anlass eine Satzphrase — oder `null`, wenn das nicht sicher geht.
 * Siehe Block oben: lieber neutral als falsch gebeugt.
 */
export function anlassPhrase(occasion: string | null | undefined, lang: BetreffSprache = "de"): AnlassPhrase | null {
  if (typeof occasion !== "string") return null;
  let roh = occasion
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, " ")
    .replace(/^\s*event-anfrage\s*:\s*/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.!,;:]+$/, "")
    .trim();
  roh = roh.replace(FUEHRENDES_WORT, "").trim();
  if (roh.length < 3 || roh.length > ANLASS_MAX) return null;

  const teile = roh.split(" ");
  let ordnung: number | null = null;
  const om = ORDNUNGSZAHL.exec(teile[0] ?? "");
  if (om) {
    ordnung = Number(om[1]);
    teile.shift();
  }
  // Hoechstens zwei Woerter (Bestimmungswort + Grundwort); alles andere ist ein Satz, kein Anlass.
  if (teile.length < 1 || teile.length > 2 || ordnung === 0) return null;
  if (!teile.every((w) => WORT.test(w))) return null;
  if (teile.length === 2 && ADJEKTIV_ENDUNG.test(teile[0]!)) return null;
  const grundwort = teile[teile.length - 1]!;

  if (lang === "en") {
    const klein = teile.map((w) => w.toLocaleLowerCase("en"));
    let text: string | null = null;
    if (klein.every((w) => EN_WOERTER.has(w))) text = klein.map((w) => (w === "christmas" ? "Christmas" : w)).join(" ");
    else if (teile.length === 1) text = DE_EN[klein[0]!] ?? null;
    if (!text) return null;
    const anlass = ordnung ? `${englischeOrdnungszahl(ordnung)} ${text}` : text;
    return { anlass, nominativ: `your ${anlass}`, akkusativ: `your ${anlass}` };
  }

  const genus = genusVon(grundwort);
  if (!genus) return null;
  // Deutsche Substantive gross — ein kleingeschriebener Freitext („weihnachtsfeier“) wird es hier.
  const woerter = teile.map((w) => w.charAt(0).toLocaleUpperCase("de-DE") + w.slice(1));
  const anlass = [ordnung ? `${ordnung}.` : null, ...woerter].filter(Boolean).join(" ");
  const nom = genus === "f" ? "Ihre" : "Ihr";
  const akk = genus === "m" ? "Ihren" : genus === "f" ? "Ihre" : "Ihr";
  return { anlass, nominativ: `${nom} ${anlass}`, akkusativ: `${akk} ${anlass}` };
}

/** „Ihren 60. Geburtstag“ bzw. neutral „Ihre Veranstaltung“ / „your event“ — nie leer. */
export function anlassAkkusativ(occasion: string | null | undefined, lang: BetreffSprache = "de"): string {
  return anlassPhrase(occasion, lang)?.akkusativ ?? (lang === "en" ? "your event" : "Ihre Veranstaltung");
}

/** „Ihr 60. Geburtstag“ bzw. neutral „Ihre Veranstaltung“ / „Your event“ — fuer Ueberschriften. */
export function anlassUeberschrift(occasion: string | null | undefined, lang: BetreffSprache = "de"): string {
  const p = anlassPhrase(occasion, lang);
  const text = p?.nominativ ?? (lang === "en" ? "your event" : "Ihre Veranstaltung");
  return text.charAt(0).toLocaleUpperCase(lang === "en" ? "en" : "de-DE") + text.slice(1);
}

/**
 * „Ihr Angebot für Ihren 60. Geburtstag am 15. August 2026 (40 Gäste)" — seit Buchungsstrecke A3
 * (29.09.2026) mit dem echten Anlass, seit B3 mit dem Namen des Gastes davor („Julia Berger, Ihr
 * Angebot für …“); ohne brauchbaren Anlass „Ihr Angebot für Ihre Veranstaltung …"
 * (vorher immer „Ihre Feier").
 *
 * Alle Platzhalter sind optional und fallen einzeln weg, ohne den Satz zu zerbrechen:
 *   * ohne Datum  -> „Ihr Angebot für Ihre Veranstaltung (40 Gäste)"
 *   * ohne Gaeste -> „Ihr Angebot für Ihre Veranstaltung am 15. August 2026"
 *   * ohne beides -> „Ihr Angebot für Ihre Veranstaltung"
 *
 * ── Was bewusst NICHT mehr drinsteht ─────────────────────────────────────────────────────────
 * Der BETRIEBSNAME (stand im alten Backend-Betreff). Der ANLASS ist seit A3 wieder drin — als Teil
 * des Satzes, nicht angehaengt. Der Betriebsname geht dabei nicht verloren: er steht als
 * Anzeigename im Absender (`resolveMailFrom`, lib/mailer.ts) und damit im Posteingang genau dort,
 * wo ein Mensch nach dem Absender sucht.
 */
export function angebotsBetreff(i: AngebotsBetreffEingabe): string {
  const de = i.lang === "de";
  const datum = betreffDatum(i.eventDate, i.lang);
  const gaeste = gaestezahlText(i.optionGuestCounts, i.guestCount);
  // A3: echter Anlass statt „Feier“; ohne brauchbaren Anlass neutral „Ihre Veranstaltung“.
  const kopf = de ? `Ihr Angebot für ${anlassAkkusativ(i.occasion, "de")}` : `Your offer for ${anlassAkkusativ(i.occasion, "en")}`;
  // B3: mit Name des Gastes vorn — „Julia Berger, Ihr Angebot für …“ / „Julia Berger, your offer for …“.
  const name = betreffName(i.kundenName);
  const mitName = name ? `${name}, ${de ? kopf : `y${kopf.slice(1)}`}` : kopf;
  const mitDatum = datum ? `${mitName}${de ? ` am ${datum}` : ` on ${datum}`}` : mitName;
  if (!gaeste) return mitDatum;
  return `${mitDatum} (${gaeste} ${de ? "Gäste" : "guests"})`;
}

/**
 * Neutraler Betreff fuer den Freitext-Versand OHNE Angebot (Punkt (b), 27.09.2026 —
 * Antoine-Korrektur an der Vorgangsseite): wird im Builder-Dialog „Angebot senden“ das Haekchen
 * „Angebot beifuegen“ abgewaehlt (`includeOffer=false`), passt der bisherige Betreff
 * (`angebotsBetreff()`, „Ihr Angebot fuer Ihre Feier …“) nicht mehr — es geht ja gerade KEIN
 * Angebot raus. „Ihre Anfrage fuer den 15. Dezember 2026“ nennt kein Angebot, das es (noch) nicht
 * gibt, und laesst wie `angebotsBetreff()` den Datumsteil weg, wenn kein Termin bekannt ist.
 *
 * Bewusst OHNE Gaestezahl: die Gaestezahl haengt an den Angebots-VARIANTEN (§5.19), die es im
 * Freitext-Pfad per Definition nicht gibt (`SendPayload.includeOffer=false` faehrt keine
 * Varianten-Logik, s. `builder.tsx`s `sendOffer()`).
 */
export function angebotsBetreffNeutral(i: Pick<AngebotsBetreffEingabe, "lang" | "eventDate">): string {
  const de = i.lang === "de";
  const datum = betreffDatum(i.eventDate, i.lang);
  const kopf = de ? "Ihre Anfrage" : "Your enquiry";
  if (!datum) return kopf;
  return de ? `${kopf} für den ${datum}` : `${kopf} for ${datum}`;
}
